import React, { useState } from 'react';
import './Pricing.css';

const Pricing: React.FC = () => {
  const [serviceType, setServiceType] = useState<'dog-boarding' | 'dog-dropin' | 'cat-dropin' | 'daycare'>('dog-dropin');
  const [numberOfPets, setNumberOfPets] = useState(1);
  const [numberOfUnits, setNumberOfUnits] = useState(1); // nights or visits
  const [medicationDoses, setMedicationDoses] = useState(0);
  const [dogSize, setDogSize] = useState<'small' | 'medium' | 'large'>('small');

  const medicationFee = 5; // per day

  const calculateTotal = () => {
    let total = 0;
    
    if (serviceType === 'dog-dropin') {
      // Dog drop-in visits pricing
      if (numberOfPets === 1) {
        const rates = [30, 55, 80, 105];
        if (numberOfUnits <= 4) {
          total = rates[numberOfUnits - 1];
        } else {
          // For more than 4 visits, use 4-visit rate + additional visits at $26.25 each
          total = 105 + (numberOfUnits - 4) * 26.25;
        }
      } else if (numberOfPets >= 2 && numberOfPets <= 4) {
        const rates = [35, 65, 95, 125];
        if (numberOfUnits <= 4) {
          total = rates[numberOfUnits - 1];
        } else {
          // For more than 4 visits, use 4-visit rate + additional visits at $31.25 each
          total = 125 + (numberOfUnits - 4) * 31.25;
        }
      } else {
        // 5 or more pets
        total = 50 * numberOfUnits;
      }
    } else if (serviceType === 'cat-dropin') {
      // Cat drop-in visits pricing
      if (numberOfPets === 1) {
        const rates = [25, 45];
        if (numberOfUnits <= 2) {
          total = rates[numberOfUnits - 1];
        } else {
          // For more than 2 visits, use 2-visit rate + additional visits at $22.50 each
          total = 45 + (numberOfUnits - 2) * 22.50;
        }
      } else if (numberOfPets >= 2 && numberOfPets <= 4) {
        const rates = [30, 55];
        if (numberOfUnits <= 2) {
          total = rates[numberOfUnits - 1];
        } else {
          // For more than 2 visits, use 2-visit rate + additional visits at $27.50 each
          total = 55 + (numberOfUnits - 2) * 27.50;
        }
      }
    } else if (serviceType === 'dog-boarding') {
      // Dog boarding pricing based on size
      const sizeRates = {
        small: 45,
        medium: 50,
        large: 55
      };
      total = sizeRates[dogSize] * numberOfUnits;
    } else if (serviceType === 'daycare') {
      // Doggie daycare
      total = 35 * numberOfUnits;
    }
    
    // Add medication fee
    total += medicationFee * medicationDoses;
    
    return total;
  };


  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <h1>Services & Pricing</h1>
        <p>Licensed • Bonded • Insured</p>
      </section>

      <section className="calculator-section">
        <div className="container">
          <h2>Cost Calculator</h2>
          <p className="section-subtitle">Estimate the cost of your pet care</p>
          
          <div className="calculator">
            <div className="calculator-inputs">
              <div className="input-group">
                <label>Service Type</label>
                <div className="button-group">
                  <button 
                    className={serviceType === 'dog-dropin' ? 'active' : ''} 
                    onClick={() => setServiceType('dog-dropin')}
                  >
                    🐕 Dog Visit
                  </button>
                  <button 
                    className={serviceType === 'cat-dropin' ? 'active' : ''} 
                    onClick={() => setServiceType('cat-dropin')}
                  >
                    🐈 Cat Visit
                  </button>
                  <button 
                    className={serviceType === 'dog-boarding' ? 'active' : ''} 
                    onClick={() => setServiceType('dog-boarding')}
                  >
                    🏠 Boarding
                  </button>
                  <button 
                    className={serviceType === 'daycare' ? 'active' : ''} 
                    onClick={() => setServiceType('daycare')}
                  >
                    ☀️ Daycare
                  </button>
                </div>
              </div>

              {serviceType === 'dog-boarding' && (
                <div className="input-group">
                  <label>Dog Size</label>
                  <div className="button-group">
                    <button 
                      className={dogSize === 'small' ? 'active' : ''} 
                      onClick={() => setDogSize('small')}
                    >
                      Small (≤20 lbs)
                    </button>
                    <button 
                      className={dogSize === 'medium' ? 'active' : ''} 
                      onClick={() => setDogSize('medium')}
                    >
                      Medium (21-50 lbs)
                    </button>
                    <button 
                      className={dogSize === 'large' ? 'active' : ''} 
                      onClick={() => setDogSize('large')}
                    >
                      Large (51-90 lbs)
                    </button>
                  </div>
                </div>
              )}

              {(serviceType === 'dog-dropin' || serviceType === 'cat-dropin') && (
                <div className="input-group">
                  <label>Number of Pets</label>
                  <div className="number-input">
                    <button onClick={() => setNumberOfPets(Math.max(1, numberOfPets - 1))}>-</button>
                    <span>{numberOfPets}</span>
                    <button onClick={() => setNumberOfPets(Math.min(10, numberOfPets + 1))}>+</button>
                  </div>
                </div>
              )}

              <div className="input-group">
                <label>
                  {serviceType === 'dog-boarding' && 'Number of nights'}
                  {serviceType === 'daycare' && 'Number of days'}
                  {(serviceType === 'dog-dropin' || serviceType === 'cat-dropin') && 'Number of visits'}
                </label>
                <div className="number-input">
                  <button onClick={() => setNumberOfUnits(Math.max(1, numberOfUnits - 1))}>-</button>
                  <span>{numberOfUnits}</span>
                  <button onClick={() => setNumberOfUnits(Math.min(30, numberOfUnits + 1))}>+</button>
                </div>
              </div>

              <div className="input-group">
                <label>Medication Doses (+$5 per day)</label>
                <div className="number-input">
                  <button onClick={() => setMedicationDoses(Math.max(0, medicationDoses - 1))}>-</button>
                  <span>{medicationDoses}</span>
                  <button onClick={() => setMedicationDoses(Math.min(20, medicationDoses + 1))}>+</button>
                </div>
                <p className="discount-note">Oral or injection administration</p>
              </div>
            </div>

            <div className="calculator-result">
              <div className="result-summary">
                <h3>Your Estimate</h3>
                <div className="estimate-details">
                  {serviceType === 'dog-dropin' && (
                    <>
                      <p><strong>{numberOfPets} dog{numberOfPets > 1 ? 's' : ''} × {numberOfUnits} visit{numberOfUnits > 1 ? 's' : ''}</strong></p>
                      {numberOfPets >= 5 && (
                        <p className="price-breakdown">$50 per visit × {numberOfUnits} = ${50 * numberOfUnits}</p>
                      )}
                    </>
                  )}
                  {serviceType === 'cat-dropin' && (
                    <p><strong>{numberOfPets} cat{numberOfPets > 1 ? 's' : ''} × {numberOfUnits} visit{numberOfUnits > 1 ? 's' : ''}</strong></p>
                  )}
                  {serviceType === 'dog-boarding' && (
                    <>
                      <p><strong>{dogSize.charAt(0).toUpperCase() + dogSize.slice(1)} dog × {numberOfUnits} night{numberOfUnits > 1 ? 's' : ''}</strong></p>
                      <p className="price-breakdown">
                        ${dogSize === 'small' && 45}{dogSize === 'medium' && 50}{dogSize === 'large' && 55} per night × {numberOfUnits} = $
                        {((dogSize === 'small' && 45) || (dogSize === 'medium' && 50) || 55) * numberOfUnits}
                      </p>
                    </>
                  )}
                  {serviceType === 'daycare' && (
                    <>
                      <p><strong>{numberOfUnits} day{numberOfUnits > 1 ? 's' : ''}</strong></p>
                      <p className="price-breakdown">$35 per day × {numberOfUnits} = ${35 * numberOfUnits}</p>
                    </>
                  )}
                  {medicationDoses > 0 && (
                    <p className="addon">+ {medicationDoses} medication dose{medicationDoses > 1 ? 's' : ''} = ${medicationDoses * 5}</p>
                  )}
                </div>
                <div className="total-price">
                  <span className="currency">$</span>
                  <span className="amount">{calculateTotal().toFixed(0)}</span>
                </div>
                <p className="estimate-note">
                  *This is an estimate. Final pricing may vary based on specific needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-cards-section">
        <div className="container">
          <h2>📋 Drop-In Visits</h2>
          <p className="section-intro">At-home visits to care for your pets while you're away</p>
          
          <h3 className="subsection-title">Dogs</h3>
          <div className="pricing-tables">
            <div className="pricing-table">
              <h4>One Dog</h4>
              <table>
                <thead>
                  <tr><th>Visits</th><th>Price</th></tr>
                </thead>
                <tbody>
                  <tr><td>1 Visit</td><td>$30</td></tr>
                  <tr><td>2 Visits</td><td>$55</td></tr>
                  <tr><td>3 Visits</td><td>$80</td></tr>
                  <tr><td>4 Visits</td><td>$105</td></tr>
                </tbody>
              </table>
            </div>
            <div className="pricing-table">
              <h4>Two to Four Dogs</h4>
              <table>
                <thead>
                  <tr><th>Visits</th><th>Price</th></tr>
                </thead>
                <tbody>
                  <tr><td>1 Visit</td><td>$35</td></tr>
                  <tr><td>2 Visits</td><td>$65</td></tr>
                  <tr><td>3 Visits</td><td>$95</td></tr>
                  <tr><td>4 Visits</td><td>$125</td></tr>
                </tbody>
              </table>
            </div>
            <div className="pricing-table">
              <h4>Five or More Dogs</h4>
              <p className="flat-rate">$50</p>
              <p className="flat-rate-label">per visit</p>
            </div>
          </div>

          <h3 className="subsection-title">Cats</h3>
          <div className="pricing-tables">
            <div className="pricing-table">
              <h4>One Cat</h4>
              <table>
                <thead>
                  <tr><th>Visits</th><th>Price</th></tr>
                </thead>
                <tbody>
                  <tr><td>1 Visit</td><td>$25</td></tr>
                  <tr><td>2 Visits</td><td>$45</td></tr>
                </tbody>
              </table>
            </div>
            <div className="pricing-table">
              <h4>Two to Four Cats</h4>
              <table>
                <thead>
                  <tr><th>Visits</th><th>Price</th></tr>
                </thead>
                <tbody>
                  <tr><td>1 Visit</td><td>$30</td></tr>
                  <tr><td>2 Visits</td><td>$55</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <h2>🏠 Overnight Boarding</h2>
          <p className="section-intro">Your dog stays in my home with all the comforts of home</p>
          <div className="pricing-tables">
            <div className="pricing-table">
              <h4>Small Dog</h4>
              <p className="size-info">(Up to 20 lbs)</p>
              <p className="flat-rate">$45</p>
              <p className="flat-rate-label">per night</p>
            </div>
            <div className="pricing-table">
              <h4>Medium Dog</h4>
              <p className="size-info">(21–50 lbs)</p>
              <p className="flat-rate">$50</p>
              <p className="flat-rate-label">per night</p>
            </div>
            <div className="pricing-table">
              <h4>Large Dog</h4>
              <p className="size-info">(51–90 lbs)</p>
              <p className="flat-rate">$55</p>
              <p className="flat-rate-label">per night</p>
            </div>
          </div>

          <h2>☀️ Doggie Daycare</h2>
          <p className="section-intro">Off-season only • Full day of fun, exercise & socialization</p>
          <div className="pricing-tables daycare-grid">
            <div className="pricing-table daycare-special">
              <p className="flat-rate">$35</p>
              <p className="flat-rate-label">per day</p>
              <ul className="daycare-info">
                <li>⏰ Hours: 8:00 AM – 8:00 PM</li>
                <li>📥 Drop-off by 8:00 AM</li>
                <li>📤 Pick-up by 8:00 PM</li>
              </ul>
            </div>
          </div>

          <h2>💊 Add-On Services</h2>
          <div className="pricing-tables addon-grid">
            <div className="pricing-table addon-special">
              <h4>Medication Administration</h4>
              <p className="addon-note">(Oral or injections)</p>
              <p className="flat-rate">$5</p>
              <p className="flat-rate-label">per day</p>
            </div>
          </div>

          <p className="pricing-footer-note">
            Prices subject to change. Holiday rates may apply.
          </p>
        </div>
      </section>

      <section className="additional-info">
        <div className="container">
          <h2>Good to Know</h2>
          <div className="info-grid">
            <div className="info-card">
              <span className="info-icon">📅</span>
              <h3>Check-in/Check-out</h3>
              <p>Drop-off and pick-up available between 8am - 8pm, 7 days a week. Please schedule your time in advance.</p>
            </div>
            <div className="info-card">
              <span className="info-icon">🍽️</span>
              <h3>Food & Supplies</h3>
              <p>Please bring your pet's regular food to maintain their diet. Also bring any medications, favorite toys, and bedding.</p>
            </div>
            <div className="info-card">
              <span className="info-icon">💉</span>
              <h3>Vaccinations Required</h3>
              <p>All pets must be up-to-date on vaccinations. Dogs require Rabies, Distemper, and Bordetella.</p>
            </div>
            <div className="info-card">
              <span className="info-icon">📝</span>
              <h3>Cancellation Policy</h3>
              <p>Please provide 48 hours notice for cancellations. Holiday reservations require 7 days notice.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
